import React, { useState, useEffect } from "react";
import Role from "@apis/role";
import Fields from "@components/fields";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronRight,
  ShieldCheck,
  LayoutDashboard,
  Package,
  ShoppingBag,
  Megaphone,
  CreditCard,
  MessageSquare,
  ShoppingCart,
  Users,
} from "lucide-react";

interface ModuleConfig {
  id: string;
  label: string;
  Icon: any;
  features: string[];
}

// Store-scoped modules only — no system-level admin modules
const storeModules: ModuleConfig[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    Icon: LayoutDashboard,
    features: ["View Dashboard"],
  },
  {
    id: "pos",
    label: "POS",
    Icon: ShoppingCart,
    features: ["Access POS", "Create Orders"],
  },
  {
    id: "orders",
    label: "Order Management",
    Icon: ShoppingBag,
    features: ["View All", "Manage Status", "Cancel Orders", "View Invoice"],
  },
  {
    id: "products",
    label: "Product Management",
    Icon: Package,
    features: ["View Products", "Add Product", "Edit Product", "Delete Product", "Manage Categories"],
  },
  {
    id: "promotions",
    label: "Promotions",
    Icon: Megaphone,
    features: ["Banners", "Coupons", "Push Notifications", "Deals"],
  },
  {
    id: "sub_admins",
    label: "Sub Admins",
    Icon: Users,
    features: ["View List", "Add New", "Edit", "Delete"],
  },
  {
    id: "roles",
    label: "Roles",
    Icon: ShieldCheck,
    features: ["View List", "Add Role", "Edit", "Delete"],
  },
  {
    id: "chat",
    label: "Chat",
    Icon: MessageSquare,
    features: ["Manage Chats"],
  },
  {
    id: "finance",
    label: "Finance",
    Icon: CreditCard,
    features: ["Withdraw Requests", "My Wallet"],
  },
];

const CreateStoreRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState(storeModules[0].id);
  const [initialName, setInitialName] = useState("");
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      fetchRoleDetails();
    }
  }, [id]);

  const fetchRoleDetails = async () => {
    try {
      const response = await Role.find(Number(id));
      const roleData = response?.data || response;
      if (roleData) {
        setInitialName(roleData.name || "");
        setSelectedPermissions(roleData.permissions || []);
      }
    } catch (error) {
      console.error("Failed to fetch role details:", error);
      toast.error("Failed to load role details");
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = (moduleId: string, feature: string) => {
    const permId = `${moduleId}.${feature}`;
    setSelectedPermissions((prev) =>
      prev.includes(permId)
        ? prev.filter((p) => p !== permId)
        : [...prev, permId],
    );
  };

  const isFeatureSelected = (moduleId: string, feature: string) => {
    return selectedPermissions.includes(`${moduleId}.${feature}`);
  };

  const isModuleFullySelected = (moduleId: string) => {
    const module = storeModules.find((m) => m.id === moduleId);
    if (!module) return false;
    return module.features.every((f) =>
      selectedPermissions.includes(`${moduleId}.${f}`),
    );
  };

  const toggleModule = (moduleId: string) => {
    const module = storeModules.find((m) => m.id === moduleId);
    if (!module) return;

    if (isModuleFullySelected(moduleId)) {
      setSelectedPermissions((prev) =>
        prev.filter((p) => !p.startsWith(`${moduleId}.`)),
      );
    } else {
      const newPerms = module.features.map((f) => `${moduleId}.${f}`);
      setSelectedPermissions((prev) => {
        const filtered = prev.filter((p) => !p.startsWith(`${moduleId}.`));
        return [...filtered, ...newPerms];
      });
    }
  };

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    if (!values.name) {
      toast.error("Please provide a name for the role.");
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        name: values.name,
        permissions: selectedPermissions,
      };

      let promise;
      if (isEdit) {
        promise = Role.update(Number(id), payload);
      } else {
        promise = Role.create(payload);
      }

      await toast.promise(promise, {
        pending: isEdit ? "Updating role..." : "Creating role...",
        success: isEdit
          ? "Role successfully updated!"
          : "Role successfully created!",
        error: isEdit ? "Failed to update role." : "Failed to save role.",
      });
      navigate("/store/roles");
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div style={{ margin: "0 auto", paddingBottom: "60px" }}>
      {/* Header Section */}
      <div
        style={{
          marginBottom: "32px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              width: "44px",
              height: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#64748b",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <ChevronRight style={{ transform: "rotate(180deg)" }} size={20} />
          </button>
          <div>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: 800,
                color: "#0f172a",
                margin: "0 0 6px 0",
                letterSpacing: "-0.025em",
              }}
            >
              {isEdit ? "Update Role" : "Create New Role"}
            </h1>
            <p
              style={{
                margin: 0,
                color: "#64748b",
                fontSize: "15px",
                fontWeight: 500,
              }}
            >
              {isEdit
                ? "Modify access boundaries and operational capabilities."
                : "Define access boundaries and operational capabilities for your store team."}
            </p>
          </div>
        </div>
      </div>

      <Formik
        initialValues={{ name: initialName }}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting }) => (
          <Form>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "320px 1fr",
                gap: "32px",
              }}
            >
              {/* Left Sidebar Pane */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "20px",
                    padding: "24px",
                    border: "1px solid #f1f5f9",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                  }}
                >
                  <Fields.Input
                    name="name"
                    label="Role Identity *"
                    placeholder="e.g. Store Manager"
                    required
                  />
                  <p
                    style={{
                      marginTop: "12px",
                      fontSize: "13px",
                      color: "#94a3b8",
                      lineHeight: "1.5",
                    }}
                  >
                    Give this role a distinct name that reflects its level of
                    authority within your store.
                  </p>
                </div>

                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "20px",
                    padding: "12px",
                    border: "1px solid #f1f5f9",
                    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      padding: "12px 16px",
                      marginBottom: "8px",
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Module Groups
                  </div>
                  {storeModules.map((module) => {
                    const isActive = activeTab === module.id;
                    const count = selectedPermissions.filter((p) =>
                      p.startsWith(`${module.id}.`),
                    ).length;
                    return (
                      <button
                        key={module.id}
                        type="button"
                        onClick={() => setActiveTab(module.id)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "12px 16px",
                          borderRadius: "12px",
                          border: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                          background: isActive ? "#fef2f2" : "transparent",
                          color: isActive ? "#f23236" : "#64748b",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          <module.Icon size={18} />
                          <span
                            style={{
                              fontWeight: isActive ? 700 : 500,
                              fontSize: "14px",
                            }}
                          >
                            {module.label}
                          </span>
                        </div>
                        {count > 0 && (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              background: isActive ? "#f23236" : "#ffffff",
                              color: isActive ? "#ffffff" : "#f23236",
                              padding: "2px 8px",
                              borderRadius: "20px",
                              border: isActive ? "none" : "1px solid #f23236",
                              transition: "all 0.2s",
                            }}
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Main Content Area */}
              <div
                style={{
                  background: "#ffffff",
                  borderRadius: "24px",
                  padding: "40px",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {storeModules.map((module) => (
                  <div
                    key={module.id}
                    style={{
                      display: activeTab === module.id ? "block" : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "32px",
                      }}
                    >
                      <div>
                        <h2
                          style={{
                            fontSize: "24px",
                            fontWeight: 800,
                            color: "#0f172a",
                            margin: "0 0 8px 0",
                          }}
                        >
                          {module.label} Permissions
                        </h2>
                        <p
                          style={{
                            margin: 0,
                            color: "#64748b",
                            fontSize: "14px",
                          }}
                        >
                          Select specific actions allowed within the{" "}
                          {module.label.toLowerCase()} module.
                        </p>
                      </div>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "10px 20px",
                          background: isModuleFullySelected(module.id)
                            ? "#fef2f2"
                            : "#f8fafc",
                          borderRadius: "14px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          border: "1.5px solid",
                          borderColor: isModuleFullySelected(module.id)
                            ? "#fecaca"
                            : "#e2e8f0",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isModuleFullySelected(module.id)}
                          onChange={() => toggleModule(module.id)}
                          style={{
                            width: "20px",
                            height: "20px",
                            accentColor: "#f23236",
                            cursor: "pointer",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: isModuleFullySelected(module.id)
                              ? "#f23236"
                              : "#475569",
                          }}
                        >
                          Enable All Access
                        </span>
                      </label>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(280px, 1fr))",
                        gap: "16px",
                      }}
                    >
                      {module.features.map((feat, i) => {
                        const isSelected = isFeatureSelected(module.id, feat);
                        return (
                          <label
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "16px",
                              padding: "20px",
                              borderRadius: "16px",
                              background: isSelected ? "#ffffff" : "#fcfcfc",
                              border: "2.5px solid",
                              borderColor: isSelected ? "#f23236" : "#f1f5f9",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              transform: isSelected
                                ? "translateY(-2px)"
                                : "none",
                              boxShadow: isSelected
                                ? "0 10px 15px -3px rgba(242, 50, 54, 0.1)"
                                : "none",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleFeature(module.id, feat)}
                              style={{
                                width: "22px",
                                height: "22px",
                                accentColor: "#f23236",
                                cursor: "pointer",
                              }}
                            />
                            <div>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: "15px",
                                  color: isSelected ? "#1e293b" : "#64748b",
                                  marginBottom: "2px",
                                }}
                              >
                                {feat}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#94a3b8" }}
                              >
                                Allow user to {feat.toLowerCase()}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Footer Actions */}
                <div
                  style={{
                    marginTop: "auto",
                    paddingTop: "40px",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "16px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    style={{
                      padding: "16px 32px",
                      borderRadius: "14px",
                      background: "#ffffff",
                      color: "#475569",
                      border: "1.5px solid #e2e8f0",
                      fontWeight: 700,
                      fontSize: "15px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    Discard Changes
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    style={{
                      padding: "16px 48px",
                      borderRadius: "14px",
                      background: "#f23236",
                      color: "#ffffff",
                      border: "none",
                      fontWeight: 700,
                      fontSize: "15px",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      boxShadow: "0 10px 20px -5px rgba(242, 50, 54, 0.3)",
                      transition: "all 0.2s",
                      opacity: isSubmitting ? 0.7 : 1,
                    }}
                  >
                    {isSubmitting
                      ? "Saving Role..."
                      : isEdit
                        ? "Update Role & Permissions"
                        : "Save Role & Permissions"}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateStoreRole;
